import { useState } from 'react';

import { AxiosError } from '@mint-lib/api';
import { TFunction, useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useMutation } from '@mint-lib/query-client';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { getRouteApi } from '@mint/routing';
import { format } from 'date-fns';
import { z } from 'zod';

import { YYYY_MM_DD } from '../../../../consts/index.js';

export const getCreateActivityValidationSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string()
        .min(1, t('Fields marked with (*) are required'))
        .max(255, t('Name should have at most 255 characters')),
      date: z.object({
        from: z.union([z.date(), z.undefined()]),
        to: z.date().optional(),
      }),
      budget: z.number().min(0, t('Budget cannot be less than 0')).nullable(),
    })
    .refine((data) => data.date.from && data.date.to, {
      message: t('Fields marked with (*) are required'),
      path: ['date'],
    });

export type CreateActivityData = z.infer<
  ReturnType<typeof getCreateActivityValidationSchema>
>;

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useCreateActivity = () => {
  const { t } = useTranslation('@myn/planning_v2');

  const [nameValidationError, setNameValidationError] = useState('');
  const [dateRangeValidationError, setDateRangeValidationError] = useState('');

  const resetValidationErrors = () => {
    setNameValidationError('');
    setDateRangeValidationError('');
  };

  const { campaignId: planId } = routerApi.useParams();

  const { mutateAsync: createActivity } = useMutation({
    mutationKey: ['createActivity'],
    mutationFn: (data: CreateActivityData) => {
      return MediaPlan.createActivity({
        data: {
          mediaPlanId: planId,
          name: data.name,
          budget: data.budget === null ? null : String(data.budget),
          dateFrom: format(data.date.from as Date, YYYY_MM_DD),
          dateTo: format(data.date.to as Date, YYYY_MM_DD),
        },
      });
    },
    onSuccess: () => {
      enqueueSnackbar({
        title: t('New activity created'),
        variant: 'success',
        removable: true,
      });
    },
    onError: (error: unknown) => {
      if (isCreateActivityValidationError(error)) {
        const validationError = error.response?.data.error;

        if (validationError?.type === 'NameValidationError') {
          setNameValidationError(validationError.message);
          return;
        }

        if (validationError?.type === 'DatesValidationError') {
          setDateRangeValidationError(validationError.message);
          return;
        }
      }

      enqueueSnackbar({
        title: t('Something went wrong'),
        subTitle: t('Failed to create activity'),
        variant: 'error',
        removable: true,
      });
    },
  });

  return {
    createActivity,
    resetValidationErrors,
    nameValidationError,
    dateRangeValidationError,
  };
};

function isCreateActivityValidationError(
  error: unknown,
): error is AxiosError<MediaPlan.CreateActivityResponse> {
  return !!(error as AxiosError<MediaPlan.CreateActivityResponse>)?.response
    ?.data?.error;
}
